import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import AppLoader from 'components/general/AppLoader';
import './PageBuilderViewer.css';

const API_BASE = process.env.REACT_APP_URL || 'http://localhost:3501';

const pickFirst = (objects, keys) => {
  for (const obj of objects) {
    if (!obj) continue;
    for (const key of keys) {
      const value = obj[key];
      if (value !== undefined && value !== null && value !== '') return value;
    }
  }
  return '';
};

const resolveMode = (meeting, meetingConfig) => {
  const modeRaw = String(pickFirst([meeting, meetingConfig], ['locationMode', 'meetingMode', 'mode', 'locationType']) || '').toLowerCase();
  if (!modeRaw) return '';
  if (modeRaw.includes('online')) return 'Online';
  if (modeRaw.includes('offline') || modeRaw.includes('in-person') || modeRaw.includes('in person')) return 'Offline';
  if (modeRaw.includes('inperson')) return 'In Person';
  return modeRaw.charAt(0).toUpperCase() + modeRaw.slice(1);
};

const resolveDayOfWeek = (meeting, meetingConfig) => {
  const explicit = pickFirst([meeting, meetingConfig], ['meetingDayOfWeek', 'dayOfWeek', 'meetingDay']);
  if (explicit) return explicit;
  const dateRaw = pickFirst([meeting], ['meetingDateISO', 'meetingDate', 'date']);
  if (!dateRaw) return '';
  const parsed = new Date(dateRaw);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString(undefined, { weekday: 'long' });
};

const getByPath = (obj, path) => {
  if (!obj || !path) return '';
  const keys = String(path).split('.').filter(Boolean);
  let current = obj;
  for (const key of keys) {
    if (current == null || typeof current !== 'object' || !(key in current)) return '';
    current = current[key];
  }
  return current ?? '';
};

const interpolateTokens = (value, dynamicData) => {
  if (typeof value !== 'string') return value;
  return value.replace(/\{\{\s*([a-zA-Z0-9_.-]+)\s*\}\}/g, (_, path) => {
    const resolved = getByPath(dynamicData, path);
    return resolved === undefined || resolved === null ? '' : String(resolved);
  });
};

const isDataUrl = (value) => /^data:/i.test(String(value || '').trim());

const resolveImageSourceUrl = (content) => {
  const original = String(content?.originalSourceUrl || '').trim();
  const current = String(content?.url || '').trim();
  if (original && !isDataUrl(original)) return original;
  if (current && !isDataUrl(current)) return current;
  return '';
};

const resolveTokensDeep = (input, dynamicData) => {
  if (typeof input === 'string') return interpolateTokens(input, dynamicData);
  if (Array.isArray(input)) return input.map((item) => resolveTokensDeep(item, dynamicData));
  if (input && typeof input === 'object') {
    return Object.fromEntries(Object.entries(input).map(([k, v]) => [k, resolveTokensDeep(v, dynamicData)]));
  }
  return input;
};

const hexToRgba = (hex, alpha) => {
  const clean = String(hex || '').replace('#', '').trim();
  if (!/^[0-9a-fA-F]{6}$/.test(clean)) return `rgba(0,65,101,${alpha})`;
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const escapeHtml = (value) => String(value || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const richTextToHtml = (value, multiline = true, allowLinks = true) => {
  const iconMatches = [];
  const iconPlaceholderPrefix = '__PB_ICON__';
  const withIconPlaceholders = String(value || '').replace(/<i\s+class=("|')([^"']+)(\1)\s*><\/i>/gi, (_, _quote, classValue) => {
    const classes = String(classValue || '')
      .trim()
      .split(/\s+/)
      .filter((token) => /^bi(?:-[a-z0-9-]+)?$/i.test(token));
    const hasBase = classes.includes('bi');
    const hasIcon = classes.some((token) => /^bi-[a-z0-9-]+$/i.test(token));
    if (!hasBase || !hasIcon) return '';

    const normalized = [
      'bi',
      ...classes.filter((token) => /^bi-[a-z0-9-]+$/i.test(token)),
    ].join(' ');
    const placeholder = `${iconPlaceholderPrefix}${iconMatches.length}__`;
    iconMatches.push({ placeholder, html: `<i class="${normalized}" aria-hidden="true"></i>` });
    return placeholder;
  });

  const escaped = escapeHtml(withIconPlaceholders);
  const withLinks = allowLinks
    ? escaped.replace(
      /\[([^\]]+)\]\((https?:\/\/[^\s)]+|\/[^\s)]+)\)/g,
      '<a href="$2" target="_blank" rel="noreferrer">$1</a>'
    )
    : escaped;
  const withBold = withLinks.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  const withItalic = withBold.replace(/\*([^*]+)\*/g, '<em>$1</em>');
  const withIcons = iconMatches.reduce((html, match) => html.replace(match.placeholder, match.html), withItalic);
  return multiline ? withIcons.replace(/\n/g, '<br/>') : withIcons;
};

const renderRichText = (value, multiline = true, allowLinks = true) => (
  <span dangerouslySetInnerHTML={{ __html: richTextToHtml(value, multiline, allowLinks) }} />
);

const fetchJsonSafe = async (url, fallback) => {
  try {
    const response = await fetch(url, { headers: { 'Content-Type': 'application/json' } });
    if (!response.ok) return fallback;
    return await response.json();
  } catch {
    return fallback;
  }
};

const resolveImageLink = (content) => {
  const linkType = String(content?.linkType || 'none').trim().toLowerCase();

  if (linkType === 'pagebuilder') {
    const pageId = String(content?.pageId || '').trim();
    if (!pageId) return null;
    return {
      href: `#pb-page:${pageId}`,
      target: '_self',
      rel: undefined
    };
  }

  if (linkType === 'app') {
    const appPath = String(content?.appPath || '').trim();
    if (!appPath) return null;
    const href = appPath.startsWith('/') || appPath.startsWith('#') ? appPath : `/${appPath}`;
    return {
      href,
      target: '_self',
      rel: undefined
    };
  }

  if (linkType === 'external') {
    const externalUrl = String(content?.externalUrl || '').trim();
    if (!externalUrl) return null;
    return {
      href: externalUrl,
      target: content?.openExternalInNewTab !== false ? '_blank' : '_self',
      rel: 'noreferrer'
    };
  }

  return null;
};

const headingTag = (level, fallback) => (/^h[1-6]$/.test(level) ? level : fallback);
const bootstrapVariant = (variant) => (
  ['primary', 'secondary', 'success', 'danger', 'warning', 'info', 'light', 'dark'].includes(variant)
    ? variant
    : 'primary'
);

function DataCardBlock({ content, dynamicData, menuBackgroundColor = '#ffffff', menuButtonColor = '#004165', menuTextColor = '#111111' }) {
  const blocks = Array.isArray(content.blocks) ? content.blocks : [];
  const TitleTag = headingTag(content.titleLevel, 'h3');

  return (
    <article className="pbv-data-card">
      {(content.title || content.subtitle) && (
        <header className="pbv-data-card-head">
          {content.title && <TitleTag>{content.icon ? <i className={`${content.icon} me-2`} aria-hidden="true" /> : null}{renderRichText(content.title, false)}</TitleTag>}
          {content.subtitle && <p>{renderRichText(content.subtitle)}</p>}
        </header>
      )}

      <div className="pbv-data-card-body">
        {blocks.map((nested) => (
          <section key={nested.id} className="pbv-data-card-block" style={{ textAlign: nested?.content?.align || 'left' }}>
            <RenderBlock block={nested} dynamicData={dynamicData} menuBackgroundColor={menuBackgroundColor} menuButtonColor={menuButtonColor} menuTextColor={menuTextColor} />
          </section>
        ))}
      </div>
    </article>
  );
}

function CarouselBlock({ content }) {
  const slides = useMemo(
    () => (content.slides || []).filter((slide) => slide?.imageUrl),
    [content.slides]
  );
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const transition = content.transition || 'slide';
  const autoPlay = content.autoPlay !== false;
  const showArrows = content.showArrows !== false;
  const showIndicators = content.showIndicators !== false;
  const pauseOnHover = content.pauseOnHover !== false;
  const loop = content.loop !== false;
  const intervalMs = Math.max(800, Number(content.intervalMs) || 3500);

  useEffect(() => {
    if (!slides.length) {
      setActiveIndex(0);
      return;
    }
    if (activeIndex > slides.length - 1) setActiveIndex(0);
  }, [slides, activeIndex]);

  useEffect(() => {
    if (!autoPlay || paused || slides.length < 2) return undefined;

    const timer = setInterval(() => {
      setActiveIndex((prev) => {
        if (prev >= slides.length - 1) {
          return loop ? 0 : prev;
        }
        return prev + 1;
      });
    }, intervalMs);

    return () => clearInterval(timer);
  }, [autoPlay, paused, slides.length, loop, intervalMs]);

  if (!slides.length) return null;

  const goPrev = () => {
    setActiveIndex((prev) => {
      if (prev === 0) return loop ? slides.length - 1 : 0;
      return prev - 1;
    });
  };

  const goNext = () => {
    setActiveIndex((prev) => {
      if (prev >= slides.length - 1) return loop ? 0 : prev;
      return prev + 1;
    });
  };

  return (
    <div className="pbv-carousel-wrap">
      {(content.title || content.subtitle) && (
        <div className="pbv-carousel-head">
          {content.title && <h3>{renderRichText(content.title, false)}</h3>}
          {content.subtitle && <p>{renderRichText(content.subtitle)}</p>}
        </div>
      )}

      <div
        className="pbv-carousel"
        onMouseEnter={() => pauseOnHover && setPaused(true)}
        onMouseLeave={() => pauseOnHover && setPaused(false)}
      >
        <div className={`pbv-carousel-viewport pbv-carousel-viewport--${transition}`}>
          {slides.map((slide, idx) => (
            <figure
              key={slide.id || idx}
              className={`pbv-carousel-slide${idx === activeIndex ? ' is-active' : ''}`}
              style={{ transform: transition === 'slide' ? `translateX(${(idx - activeIndex) * 100}%)` : undefined }}
            >
              <img src={slide.imageUrl} alt={slide.caption || `slide-${idx + 1}`} />
              {slide.caption && <figcaption>{renderRichText(slide.caption)}</figcaption>}
            </figure>
          ))}
        </div>

        {showArrows && slides.length > 1 && (
          <>
            <button type="button" className="pbv-carousel-arrow pbv-carousel-arrow--prev" onClick={goPrev} aria-label="Previous slide">
              <i className="bi bi-chevron-left" aria-hidden="true" />
            </button>
            <button type="button" className="pbv-carousel-arrow pbv-carousel-arrow--next" onClick={goNext} aria-label="Next slide">
              <i className="bi bi-chevron-right" aria-hidden="true" />
            </button>
          </>
        )}

        {showIndicators && slides.length > 1 && (
          <div className="pbv-carousel-dots" role="tablist" aria-label="Carousel slide indicators">
            {slides.map((slide, idx) => (
              <button
                key={slide.id || `dot-${idx}`}
                type="button"
                className={`pbv-carousel-dot${idx === activeIndex ? ' is-active' : ''}`}
                aria-label={`Go to slide ${idx + 1}`}
                aria-current={idx === activeIndex ? 'true' : undefined}
                onClick={() => setActiveIndex(idx)}
              />
            ))}
          </div>
        )}

      </div>
    </div>
  );
}

function RenderBlock({ block, dynamicData, menuBackgroundColor = '#ffffff', menuButtonColor = '#004165', menuTextColor = '#111111' }) {
  const content = resolveTokensDeep(block?.content || {}, dynamicData);
  const type = block?.type;

  switch (type) {
    case 'heading': {
      const Tag = content.level || 'h2';
      return <Tag>{content.icon ? <i className={`${content.icon} me-2`} aria-hidden="true" /> : null}{renderRichText(content.text || '', false)}</Tag>;
    }
    case 'paragraph':
      return <p style={{ whiteSpace: 'pre-wrap' }}>{renderRichText(content.text || content.body || '')}</p>;
    case 'text':
      return content.body ? <div dangerouslySetInnerHTML={{ __html: content.body }} /> : null;
    case 'html':
      return content.html ? <div dangerouslySetInnerHTML={{ __html: content.html }} /> : null;
    case 'hero': {
      const heroLayers = Array.isArray(content.heroLayers) ? content.heroLayers.slice(0, 3) : [];
      const hasLayerImages = heroLayers.some((layer) => layer?.url);
      const hasLegacyBackground = !!content.backgroundUrl;

      return (
        <section
          className="pbv-hero"
          style={{
            padding: '2rem 1rem',
            borderRadius: 12,
            position: 'relative',
            overflow: 'hidden',
            backgroundColor: content.heroBackgroundColor || 'rgba(0, 65, 101, 0.12)',
            backgroundImage: hasLegacyBackground
              ? `linear-gradient(rgba(0,0,0,${content.overlayOpacity ?? 0.35}), rgba(0,0,0,${content.overlayOpacity ?? 0.35})), url(${content.backgroundUrl})`
              : undefined,
            backgroundSize: hasLegacyBackground ? 'cover' : undefined,
            backgroundPosition: hasLegacyBackground ? 'right bottom' : undefined,
            backgroundRepeat: hasLegacyBackground ? 'no-repeat' : undefined,
          }}
        >
          {hasLayerImages && (
            <div className="pbv-hero-layers" aria-hidden="true">
              <div className="pbv-hero-layers-stage">
                {heroLayers.map((layer, idx) => (
                  layer?.url ? (
                    <img
                      key={layer.id || `hero-layer-${idx}`}
                      src={layer.url}
                      alt=""
                      className="pbv-hero-layer"
                      style={{
                        left: `${Number(layer.x ?? 70)}%`,
                        top: `${Number(layer.y ?? 55)}%`,
                        width: `${Number(layer.width ?? 24)}%`,
                        opacity: Number(layer.opacity ?? 1),
                        zIndex: idx + 1,
                      }}
                    />
                  ) : null
                ))}
              </div>
            </div>
          )}

          <div style={{ position: 'relative', zIndex: 6 }}>
            {content.title && <h2 style={{ color: content.titleColor || '#ffffff' }}>{renderRichText(content.title, false)}</h2>}
            {content.subtitle && <p style={{ color: content.subtitleColor || '#ffffff' }}>{renderRichText(content.subtitle)}</p>}
          </div>
        </section>
      );
    }
    case 'banner':
      return (
        <section
          className="pbv-hero"
          style={{
            padding: '2rem 1rem',
            borderRadius: 12,
            backgroundColor: 'rgba(0, 65, 101, 0.12)',
            backgroundImage: content.backgroundUrl
              ? `linear-gradient(rgba(0,0,0,${content.overlayOpacity ?? 0.35}), rgba(0,0,0,${content.overlayOpacity ?? 0.35})), url(${content.backgroundUrl})`
              : undefined,
            backgroundSize: content.backgroundUrl ? 'cover' : undefined,
            backgroundPosition: content.backgroundUrl ? 'right bottom' : undefined,
            backgroundRepeat: content.backgroundUrl ? 'no-repeat' : undefined,
          }}
        >
          {content.title && <h2 style={{ color: content.titleColor || '#ffffff' }}>{renderRichText(content.title, false)}</h2>}
          {content.subtitle && <p style={{ color: content.subtitleColor || '#ffffff' }}>{renderRichText(content.subtitle)}</p>}
        </section>
      );
    case 'image':
      {
        const borderRadius = Math.max(0, Number(content.borderRadius ?? 10));
        const imageAlign = ['left', 'center', 'right'].includes(String(content.align || '').toLowerCase())
          ? String(content.align).toLowerCase()
          : 'left';
        const resizeUnit = content.resizeUnit === '%' ? '%' : 'px';
        const resizeWidthRaw = Number(content.resizeWidth ?? content.widthPercent ?? 100);
        const resizeHeightRaw = Number(content.resizeHeight ?? content.displayHeight ?? 320);
        const resizeBaseWidth = Math.min(12000, Math.max(1, Number(content.resizeBaseWidth ?? content.resizedWidthPx ?? 1024)));
        const resizeBaseHeight = Math.min(12000, Math.max(1, Number(content.resizeBaseHeight ?? content.resizedHeightPx ?? 1024)));
        const resizeWidth = resizeUnit === 'px'
          ? Math.min(5000, Math.max(1, Number.isFinite(resizeWidthRaw) ? resizeWidthRaw : 1024))
          : Math.min(500, Math.max(1, Number.isFinite(resizeWidthRaw) ? resizeWidthRaw : 100));
        const resizeHeight = resizeUnit === 'px'
          ? Math.min(5000, Math.max(1, Number.isFinite(resizeHeightRaw) ? resizeHeightRaw : 1024))
          : Math.min(500, Math.max(1, Number.isFinite(resizeHeightRaw) ? resizeHeightRaw : 100));
        const targetWidthPx = resizeUnit === 'px'
          ? Math.round(resizeWidth)
          : Math.max(1, Math.round((resizeBaseWidth * resizeWidth) / 100));
        const targetHeightPx = resizeUnit === 'px'
          ? Math.round(resizeHeight)
          : Math.max(1, Math.round((resizeBaseHeight * resizeHeight) / 100));
        const imageUrl = resolveImageSourceUrl(content);
        const linkConfig = resolveImageLink(content);
        const imageNode = imageUrl ? (
          <img
            src={imageUrl}
            alt={content.alt || content.caption || 'image'}
            style={{
              width: `${targetWidthPx}px`,
              height: `${targetHeightPx}px`,
              maxWidth: '100%',
              borderRadius,
              display: 'block',
              marginLeft: imageAlign === 'center' ? 'auto' : imageAlign === 'right' ? 'auto' : 0,
              marginRight: imageAlign === 'center' ? 'auto' : imageAlign === 'right' ? 0 : 'auto',
            }}
          />
        ) : null;

        return (
          <figure style={{ margin: 0, textAlign: imageAlign }}>
            {linkConfig && imageNode ? (
              <a href={linkConfig.href} target={linkConfig.target} rel={linkConfig.rel}>
                {imageNode}
              </a>
            ) : imageNode}
            {content.caption && <figcaption style={{ marginTop: 8 }}>{renderRichText(content.caption)}</figcaption>}
          </figure>
        );
      }
    case 'carousel':
      return <CarouselBlock content={content} />;
    case 'collage':
      return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
          {(content.images || []).map((img) => (
            <img key={img.id} src={img.url} alt={img.alt || 'collage'} style={{ width: '100%', borderRadius: 8 }} />
          ))}
        </div>
      );
    case 'video':
      return content.embedUrl ? <iframe title={content.title || 'video'} src={content.embedUrl} style={{ width: '100%', minHeight: 360, border: 0, borderRadius: 10 }} allowFullScreen /> : null;
    case 'audio':
      return content.url ? <audio controls src={content.url} style={{ width: '100%' }} /> : null;
    case 'pdf':
      return content.url ? <iframe title={content.title || 'pdf'} src={content.url} style={{ width: '100%', minHeight: content.height || 480, border: 0, borderRadius: 10 }} /> : null;
    case 'quote':
      return (
        <blockquote style={{ borderLeft: '4px solid var(--tm-blue, #004165)', paddingLeft: 12 }}>
          <p>{content.icon ? <i className={`${content.icon} me-2`} aria-hidden="true" /> : null}{renderRichText(content.text || '')}</p>
          {content.author && <footer>- {renderRichText(content.author, false)}</footer>}
        </blockquote>
      );
    case 'callout':
      return <div style={{ padding: '0.8rem 1rem', borderRadius: 8, background: 'rgba(0,65,101,0.1)' }}>{content.icon ? <i className={`${content.icon} me-2`} aria-hidden="true" /> : null}{renderRichText(content.text || '')}</div>;
    case 'highlightBox':
      return <div style={{ padding: '0.8rem 1rem', borderRadius: 8, background: 'rgba(0,65,101,0.1)' }}>{renderRichText(content.text || '')}</div>;
    case 'announcement':
      return <div style={{ padding: '0.8rem 1rem', borderRadius: 8, background: 'rgba(0,65,101,0.1)' }}>{content.icon ? <i className={`${content.icon} me-2`} aria-hidden="true" /> : null}{renderRichText(content.text || '')}</div>;
    case 'badge':
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {(content.items || []).map((item) => (
            <span key={item.id} className="badge pbv-badge">{item.icon ? <i className={`${item.icon} me-1`} aria-hidden="true" /> : null}{renderRichText(item.text || '', false)}</span>
          ))}
        </div>
      );
    case 'bulletList':
      return content.style === 'numbered' ? (
        <ol>{(content.items || []).map((item) => <li key={item.id}>{renderRichText(item.text || '')}</li>)}</ol>
      ) : (
        <ul>{(content.items || []).map((item) => <li key={item.id}>{renderRichText(item.text || '')}</li>)}</ul>
      );
    case 'iconList':
      return (
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {(content.items || []).map((item) => (
            <li key={item.id} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <i className={item.icon || 'bi bi-dot'} aria-hidden="true" />
              <span>{renderRichText(item.text || '')}</span>
            </li>
          ))}
        </ul>
      );
    case 'faq':
    case 'accordion':
      return (
        <div style={{ display: 'grid', gap: 8 }}>
          {(content.items || []).map((item) => {
            const isAccordionWithBlocks = type === 'accordion' && Array.isArray(item.blocks);
            const itemBlocks = isAccordionWithBlocks ? item.blocks : [];
            return (
              <details key={item.id}>
                <summary>{item.icon ? <i className={`${item.icon} me-2`} aria-hidden="true" /> : null}{renderRichText(item.question || item.title || 'Item', false)}</summary>
                <div style={{ marginTop: 6 }}>
                  {isAccordionWithBlocks && itemBlocks.length > 0 ? (
                    <div style={{ display: 'grid', gap: 8 }}>
                      {itemBlocks.map((nested) => (
                        <div key={nested.id} style={{ textAlign: nested?.content?.align || 'left' }}>
                          <RenderBlock block={nested} dynamicData={dynamicData} menuBackgroundColor={menuBackgroundColor} menuButtonColor={menuButtonColor} menuTextColor={menuTextColor} />
                        </div>
                      ))}
                    </div>
                  ) : (
                    renderRichText(item.answer || item.body || '')
                  )}
                </div>
              </details>
            );
          })}
        </div>
      );
    case 'toggleBlock':
      return (
        <details>
          <summary>{renderRichText(content.label || 'Details', false)}</summary>
          <div style={{ marginTop: 6 }}>{renderRichText(content.content || '')}</div>
        </details>
      );
    case 'columns':
      return (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.max(1, content.count || 2)}, minmax(0, 1fr))`, gap: 10 }}>
          {(content.cols || []).map((col) => (
            <div key={col.id} style={{ padding: 10, border: '1px solid rgba(128,128,128,0.25)', borderRadius: 8 }}>
              {col.title && <h5>{renderRichText(col.title, false)}</h5>}
              <div>{renderRichText(col.content || '')}</div>
            </div>
          ))}
        </div>
      );
    case 'cardGrid': {
      const columnCount = Math.max(1, Number(content.columns) || 3);
      const xlSpan = Math.max(1, Math.floor(12 / columnCount));
      const mdColumnCount = columnCount >= 4 ? columnCount / 2 : columnCount;
      const mdSpan = Math.max(1, Math.floor(12 / mdColumnCount));

      return (
        <Row className="g-3">
          {(content.cards || []).map((card) => {
            const TitleTag = headingTag(card.titleLevel, 'h5');
            const buttonVariant = bootstrapVariant(card.buttonVariant);
            return (
              <Col key={card.id} xs={12} md={mdSpan} xl={xlSpan}>
                <article style={{ border: '1px solid rgba(128,128,128,0.25)', borderRadius: 8, padding: 10, height: '100%' }}>
                  {card.imageUrl && <img src={card.imageUrl} alt={card.title || 'card'} style={{ width: '100%', borderRadius: 8, marginBottom: 8 }} />}
                  {card.title && <TitleTag>{card.icon ? <i className={`${card.icon} me-2`} aria-hidden="true" /> : null}{renderRichText(card.title, false)}</TitleTag>}
                  <p>{renderRichText(card.body || '')}</p>
                  {card.buttonLabel && card.buttonUrl && <a href={card.buttonUrl} target="_blank" rel="noreferrer" className={`btn btn-${buttonVariant}`}>{renderRichText(card.buttonLabel, false, false)}</a>}
                </article>
              </Col>
            );
          })}
        </Row>
      );
    }
    case 'dataCard':
      return <DataCardBlock content={content} dynamicData={dynamicData} menuBackgroundColor={menuBackgroundColor} menuButtonColor={menuButtonColor} menuTextColor={menuTextColor} />;
    case 'button':
      return content.url ? <a href={content.url} target={content.openNewTab ? '_blank' : '_self'} rel="noreferrer" className="btn btn-primary">{renderRichText(content.label || 'Open', false, false)}</a> : null;
    case 'breadcrumbs':
      return (
        <nav>
          {(content.items || []).map((item, idx) => (
            <span key={item.id || idx}>
              {idx > 0 ? ' / ' : ''}
              {item.url ? <a href={item.url}>{item.icon ? <i className={`${item.icon} me-1`} aria-hidden="true" /> : null}{renderRichText(item.label || '', false, false)}</a> : <>{item.icon ? <i className={`${item.icon} me-1`} aria-hidden="true" /> : null}{renderRichText(item.label || '', false)}</>}
            </span>
          ))}
        </nav>
      );
    case 'navMenu':
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {(content.items || []).map((item) => (
            <a key={item.id} href={item.url} target={item.openNewTab ? '_blank' : '_self'} rel="noreferrer">{renderRichText(item.label || '', false, false)}</a>
          ))}
        </div>
      );
    case 'submenu':
      return (
        <div style={{ backgroundColor: menuBackgroundColor, padding: '0.5rem 0', borderRadius: '4px' }}>
          <strong style={{ color: menuTextColor, display: 'block', padding: '0.5rem 1rem' }}>{renderRichText(content.parentLabel || 'Menu', false)}</strong>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {(content.items || []).map((item) => (
              <li key={item.id} style={{ margin: 0, padding: 0 }}>
                <a href={item.url} style={{ display: 'block', padding: '0.5rem 1rem', color: menuButtonColor, textDecoration: 'none', transition: 'background-color 0.2s' }} onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(0,0,0,0.1)'} onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}>
                  {renderRichText(item.label || '', false, false)}
                </a>
              </li>
            ))}
          </ul>
        </div>
      );
    case 'footer':
      return (
        <footer style={{ padding: '1rem 0', borderTop: '1px solid rgba(128,128,128,0.3)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
            {(content.cols || []).map((col) => (
              <div key={col.id}>
                <strong>{renderRichText(col.title || '', false)}</strong>
                <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{col.links || ''}</pre>
              </div>
            ))}
          </div>
          <small>{renderRichText(content.copyright || '', false)}</small>
        </footer>
      );
    case 'countdown': {
      const target = content.targetDate ? new Date(content.targetDate).getTime() : null;
      const diff = target ? target - Date.now() : null;
      const days = diff && diff > 0 ? Math.floor(diff / (1000 * 60 * 60 * 24)) : 0;
      return <div><strong>{content.icon ? <i className={`${content.icon} me-2`} aria-hidden="true" /> : null}{renderRichText(content.title || 'Countdown', false)}:</strong> {diff && diff > 0 ? `${days} day(s) left` : renderRichText(content.completedMessage || 'Time is up!', false)}</div>;
    }
    case 'poll':
      return (
        <div>
          <h5>{renderRichText(content.question || 'Poll', false)}</h5>
          <ul>{(content.options || []).map((opt) => <li key={opt.id}>{renderRichText(opt.text || '')}</li>)}</ul>
        </div>
      );
    case 'divider':
      return <hr />;
    case 'agendaSnippet':
      return <p>Agenda snippet {content.label ? renderRichText(`- ${content.label}`) : ''}</p>;
    default:
      return null;
  }
}

export default function PageBuilderViewer({ initialPageId = null, onPageChange }) {
  const onPageChangeRef = useRef(onPageChange);
  const [pages, setPages] = useState([]);
  const [menu, setMenu] = useState([]);
  const [menuBackgroundColor, setMenuBackgroundColor] = useState('#ffffff');
  const [menuButtonColor, setMenuButtonColor] = useState('#004165');
  const [menuTextColor, setMenuTextColor] = useState('#111111');
  const [clubData, setClubData] = useState(null);
  const [nextMeeting, setNextMeeting] = useState(null);
  const [meetingConfig, setMeetingConfig] = useState(null);
  const [activePageId, setActivePageId] = useState(initialPageId);
  const [openSubmenuId, setOpenSubmenuId] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dynamicLoading, setDynamicLoading] = useState(true);

  useEffect(() => {
    onPageChangeRef.current = onPageChange;
  }, [onPageChange]);

  const load = useCallback(async () => {
    setLoading(true);
    setDynamicLoading(true);

    try {
      const [pagesData, menuData] = await Promise.all([
        fetchJsonSafe(`${API_BASE}/pagebuilder/pages`, []),
        fetchJsonSafe(`${API_BASE}/pagebuilder/menu`, { items: [] }),
      ]);

      setPages(Array.isArray(pagesData) ? pagesData : []);
      setMenu(Array.isArray(menuData?.items) ? menuData.items : []);
      setMenuBackgroundColor(menuData?.menuBackgroundColor || '#ffffff');
      setMenuButtonColor(menuData?.menuButtonColor || '#004165');
      setMenuTextColor(menuData?.menuTextColor || '#111111');
    } finally {
      setLoading(false);
    }

    try {
      const [clubRaw, meetingRaw, meetingConfigRaw] = await Promise.all([
        fetchJsonSafe(`${API_BASE}/club`, null),
        fetchJsonSafe(`${API_BASE}/meetings/comingup`, null),
        fetchJsonSafe(`${API_BASE}/meetingsConfig`, null),
      ]);

      setClubData(Array.isArray(clubRaw) ? (clubRaw[0] || null) : clubRaw);
      setNextMeeting(meetingRaw || null);
      setMeetingConfig(meetingConfigRaw || null);
    } finally {
      setDynamicLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (initialPageId) setActivePageId(initialPageId);
  }, [initialPageId]);

  useEffect(() => {
    const handlePageBuilderUpdated = () => {
      load();
    };

    window.addEventListener('tm-pagebuilder-updated', handlePageBuilderUpdated);
    return () => window.removeEventListener('tm-pagebuilder-updated', handlePageBuilderUpdated);
  }, [load]);

  useEffect(() => {
    const handleInternalPageLink = (event) => {
      const anchor = event.target?.closest?.('a[href^="#pb-page:"]');
      if (!anchor) return;

      event.preventDefault();
      const href = anchor.getAttribute('href') || '';
      const pageId = href.replace('#pb-page:', '').trim();
      if (!pageId) return;

      setActivePageId(pageId);
      setOpenSubmenuId(null);
      setMobileMenuOpen(false);
    };

    document.addEventListener('click', handleInternalPageLink);
    return () => document.removeEventListener('click', handleInternalPageLink);
  }, []);

  const firstPageId = useMemo(() => {
    if (menu.length > 0) return menu[0].pageId;
    if (pages.length > 0) return pages[0].id;
    return null;
  }, [menu, pages]);

  const activePageExists = pages.some((page) => page.id === activePageId);
  const effectivePageId = activePageExists ? activePageId : firstPageId;
  const activePage = pages.find((p) => p.id === effectivePageId) || null;

  useEffect(() => {
    const pageTitle = activePage?.title?.trim();
    if (!pageTitle) return undefined;

    const previousDocumentTitle = document.title;
    const titleMetaDefinitions = [
      { selector: 'meta[property="og:title"]', attribute: 'property', value: 'og:title' },
      { selector: 'meta[name="twitter:title"]', attribute: 'name', value: 'twitter:title' }
    ];
    const titleMetaElements = titleMetaDefinitions.map(({ selector, attribute, value }) => {
      const existingElement = document.querySelector(selector);
      if (existingElement) {
        return { element: existingElement, content: existingElement.getAttribute('content'), created: false };
      }

      const element = document.createElement('meta');
      element.setAttribute(attribute, value);
      document.head.appendChild(element);
      return { element, content: null, created: true };
    });

    document.title = pageTitle;
    titleMetaElements.forEach(({ element }) => element.setAttribute('content', pageTitle));

    return () => {
      document.title = previousDocumentTitle;
      titleMetaElements.forEach(({ element, content, created }) => {
        if (created) {
          element.remove();
        } else if (content === null) {
          element.removeAttribute('content');
        } else {
          element.setAttribute('content', content);
        }
      });
    };
  }, [activePage?.title]);

  useEffect(() => {
    if (effectivePageId) onPageChangeRef.current?.(effectivePageId);
  }, [effectivePageId]);

  const copyPageLink = async () => {
    const shareUrl = new URL(window.location.href);
    shareUrl.searchParams.set('pageBuilderPage', effectivePageId);
    shareUrl.hash = '';

    try {
      await navigator.clipboard.writeText(shareUrl.toString());
      toast.success('Page link copied!', { position: 'bottom-left' });
    } catch {
      toast.error('Unable to copy the page link.', { position: 'bottom-left' });
    }
  };

  const dynamicData = useMemo(() => {
    const clubFields = {
      name: pickFirst([clubData], ['name', 'clubName']),
      slogan: pickFirst([clubData], ['slogan', 'tagline']),
      email: pickFirst([clubData], ['email', 'contactEmail']),
      website: pickFirst([clubData], ['website', 'websiteUrl']),
      phone: pickFirst([clubData], ['phone', 'contactPhone']),
      city: pickFirst([clubData], ['city']),
      country: pickFirst([clubData], ['country']),
    };

    const nextMeetingId = pickFirst([nextMeeting], ['_id', 'id']);
    const nextMeetingLink = nextMeetingId ? `/meeting/${nextMeetingId}` : '';

    const meetingFields = {
      location: pickFirst([nextMeeting, meetingConfig], ['meetingLocation', 'location', 'meetingVenue']),
      dayOfWeek: resolveDayOfWeek(nextMeeting, meetingConfig),
      frequency: pickFirst([meetingConfig, nextMeeting], ['meetingFrequency', 'frequency']),
      time: pickFirst([nextMeeting, meetingConfig], ['meetingTime', 'time', 'meetingStartTime']),
      mode: resolveMode(nextMeeting, meetingConfig),
      address: pickFirst([nextMeeting, meetingConfig], ['meetingAddress', 'address', 'locationAddress']),
      mapLink: pickFirst([nextMeeting, meetingConfig], ['inPersonMapsLink', 'mapLink', 'mapsLink', 'meetingMapLink']),
      onlineLink: pickFirst([nextMeeting, meetingConfig], ['onlineMeetingLink', 'meetingOnlineLink', 'zoomLink', 'meetingLink']),
      passcode: pickFirst([nextMeeting, meetingConfig], ['meetingPasscode', 'passcode', 'zoomPasscode']),
      nextMeetingLink,
      nextMeetingAttendanceLink: pickFirst([nextMeeting, meetingConfig], ['attendanceLink', 'meetingAttendanceLink', 'nextMeetingAttendanceLink']),
    };

    return { club: clubFields, meeting: meetingFields };
  }, [clubData, nextMeeting, meetingConfig]);

  const menuTree = useMemo(() => {
    const source = Array.isArray(menu) ? menu : [];

    const explicitByParent = source.some((item) => item?.parentPageId);
    if (explicitByParent) {
      const nodeMap = new Map(source.map((item) => [item.pageId, { ...item, children: [] }]));
      const roots = [];

      source.forEach((item) => {
        const node = nodeMap.get(item.pageId);
        if (!item.parentPageId || item.parentPageId === item.pageId || !nodeMap.has(item.parentPageId)) {
          roots.push(node);
          return;
        }
        nodeMap.get(item.parentPageId).children.push(node);
      });

      return roots;
    }

    // 1) explicit nested menu support: { children: [...] }
    const explicitNested = source.some((item) => Array.isArray(item?.children) && item.children.length > 0);
    if (explicitNested) return source;

    // 2) label convention support: "Parent > Child" or "Parent / Child"
    const separators = [' > ', ' / '];
    const top = [];
    const parentMap = new Map();

    source.forEach((item) => {
      const label = item?.label || '';
      const sep = separators.find((s) => label.includes(s));

      if (!sep) {
        top.push({ ...item, children: item.children || [] });
        return;
      }

      const [parentLabelRaw, childLabelRaw] = label.split(sep);
      const parentLabel = (parentLabelRaw || '').trim();
      const childLabel = (childLabelRaw || '').trim();
      if (!parentLabel || !childLabel) {
        top.push({ ...item, children: item.children || [] });
        return;
      }

      if (!parentMap.has(parentLabel)) {
        const parentNode = {
          pageId: `submenu-${parentLabel.toLowerCase().replace(/\s+/g, '-')}`,
          label: parentLabel,
          children: [],
        };
        parentMap.set(parentLabel, parentNode);
        top.push(parentNode);
      }

      parentMap.get(parentLabel).children.push({ ...item, label: childLabel });
    });

    return top;
  }, [menu]);

  const menuItemStyle = (isActive = false) => ({
    backgroundColor: hexToRgba(menuButtonColor, isActive ? 0.3 : 0.16),
    borderColor: hexToRgba(menuButtonColor, isActive ? 0.8 : 0.5),
    color: menuTextColor,
    fontWeight: isActive ? 700 : 500,
  });

  const submenuItemStyle = (isActive = false) => ({
    backgroundColor: isActive ? hexToRgba(menuButtonColor, 0.24) : 'transparent',
    color: menuTextColor,
    fontWeight: isActive ? 700 : 500,
  });

  const pageIdSet = useMemo(() => new Set((pages || []).map((page) => page.id)), [pages]);

  if (loading) {
    return (
      <div className="pagebuilder-viewer pagebuilder-viewer-loading">
        <AppLoader lines={4} label="Loading page content..." />
      </div>
    );
  }

  if (!activePage) {
    return <div className="text-center py-5 pagebuilder-viewer">No published page-builder content found.</div>;
  }

  return (
    <div className="pagebuilder-viewer">
      {mobileMenuOpen && (
        <button
          type="button"
          className="pbv-mobile-overlay"
          aria-label="Close menu overlay"
          onClick={() => {
            setMobileMenuOpen(false);
            setOpenSubmenuId(null);
          }}
        />
      )}

      {menuTree.length > 0 && (
        <div className="pbv-nav-wrap">
          <button
            type="button"
            className="pbv-mobile-menu-toggle"
            style={{
              backgroundColor: hexToRgba(menuButtonColor, 0.14),
              borderColor: hexToRgba(menuButtonColor, 0.55),
              color: menuTextColor,
            }}
            onClick={() => {
              setMobileMenuOpen((prev) => !prev);
              setOpenSubmenuId(null);
            }}
            aria-expanded={mobileMenuOpen}
            aria-controls="pbv-nav"
            aria-label="Toggle page menu"
          >
            <i className="bi bi-list" aria-hidden="true" />
            <span>Menu</span>
          </button>

          <nav id="pbv-nav" className={`pbv-nav${mobileMenuOpen ? ' pbv-nav--open' : ''}`} aria-label="Page builder navigation" style={{ backgroundColor: menuBackgroundColor }}>
            {menuTree.map((item) => {
              const hasChildren = Array.isArray(item.children) && item.children.length > 0;

              if (!hasChildren) {
                return (
                  <button
                    key={item.pageId}
                    type="button"
                    className={`pbv-nav-item${item.pageId === effectivePageId ? ' pbv-nav-item--active' : ''}`}
                    style={menuItemStyle(item.pageId === effectivePageId)}
                    onClick={() => {
                      setActivePageId(item.pageId);
                      setOpenSubmenuId(null);
                      setMobileMenuOpen(false);
                    }}
                  >
                    {item.icon ? <i className={item.icon} aria-hidden="true" /> : null}
                    {item.label}
                  </button>
                );
              }

              const submenuEntries = [];
              const seenPageIds = new Set();

              if (pageIdSet.has(item.pageId)) {
                submenuEntries.push({ pageId: item.pageId, label: item.label, icon: item.icon || '' });
                seenPageIds.add(item.pageId);
              }

              item.children.forEach((child) => {
                if (!child?.pageId || seenPageIds.has(child.pageId)) return;
                submenuEntries.push(child);
                seenPageIds.add(child.pageId);
              });

              return (
                <div key={item.pageId} className="pbv-nav-group">
                  <button
                    type="button"
                    className="pbv-nav-item pbv-nav-item--parent"
                    style={menuItemStyle(openSubmenuId === item.pageId)}
                    onClick={() => setOpenSubmenuId((prev) => (prev === item.pageId ? null : item.pageId))}
                    aria-expanded={openSubmenuId === item.pageId}
                    aria-haspopup="menu"
                  >
                    {item.icon ? <i className={item.icon} aria-hidden="true" /> : null}
                    {item.label}
                    <i className={`bi ${openSubmenuId === item.pageId ? 'bi-chevron-up' : 'bi-chevron-down'}`} aria-hidden="true" />
                  </button>
                  {openSubmenuId === item.pageId && (
                    <div className="pbv-submenu" role="menu" style={{ backgroundColor: menuBackgroundColor, backgroundImage: 'none' }}>
                      {submenuEntries.map((child) => (
                        <button
                          key={child.pageId}
                          type="button"
                          role="menuitem"
                          className={`pbv-submenu-item${child.pageId === effectivePageId ? ' pbv-submenu-item--active' : ''}`}
                          style={submenuItemStyle(child.pageId === effectivePageId)}
                          onClick={() => {
                            setActivePageId(child.pageId);
                            setOpenSubmenuId(null);
                            setMobileMenuOpen(false);
                          }}
                        >
                          {child.icon ? <i className={child.icon} aria-hidden="true" /> : null}
                          {child.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </div>
      )}

      <div className="pbv-page-actions">
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={copyPageLink}>
          <i className="bi bi-link-45deg me-1" aria-hidden="true" />
          Copy page link
        </button>
      </div>

      <div className="pbv-content-stack" style={{ display: 'grid', gap: 14 }}>
        {(activePage.blocks || []).filter((block) => block?.isVisible !== false).map((block) => (
          <section key={block.id} className={`pbv-block pbv-block--${block.type}`} style={{ textAlign: block?.content?.align || 'left' }}>
            {dynamicLoading ? <div className="pbv-block-loader"><AppLoader lines={2} size="sm" label="Loading block data..." /></div> : <RenderBlock block={block} dynamicData={dynamicData} menuBackgroundColor={menuBackgroundColor} menuButtonColor={menuButtonColor} menuTextColor={menuTextColor} />}
          </section>
        ))}
      </div>
    </div>
  );
}
